// "Scelta connessione": screw family (with the catalog photos), hole type, diameter and length (lists filtered by the
// engine), washer and automatic minimum distances.
import { memo, useId, type FC } from "react";
import { FormField } from "../../../components/ds2/FormField";
import { MessageBox } from "../../../components/ds2/MessageBox";
import { Select } from "../../../components/ds2/Select";
import { Switch } from "../../../components/ds2/Switch";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import Html from "./Html";
import { useMissing } from "./missingFields";
import Note from "./Note";
import { withoutWarningSign } from "./notes";
import { stringOptions, type EngineView } from "./uiState";
import { useScForm } from "./useScForm";
import FamilyCell from "./FamilyCell";
import styles from "./ConnectionCard.module.css";

export type ScrewLevel = "family" | "diameter";

type ConnectionCardProps = {
  view: EngineView;
  // family / diameter changed: the page resolves the screw (first admissible diameter / length, as the HTML lists)
  onScrewChange?: (level: ScrewLevel, value: string) => void;
};

const ConnectionCard: FC<ConnectionCardProps> = ({ view, onScrewChange }) => {
  const disabled = useReadOnly();
  const calc = useI18n().translation.screwConnectorModule.calculation;
  const t = calc.connectionSection;
  const notes = calc.notes;
  const { values, set } = useScForm();
  const isMissing = useMissing();
  const { ui, options } = view;
  const ev = view.values;
  const switchId = useId();

  const family = ev?.etaFamiglia ?? "";
  const familyOptions = options.families.map((value) => ({ value, label: value }));
  const foro = ev ? ev.etaForo : values.preDrilled ? "predrilled" : "nonPredrilled";
  const foroOptions = [
    { value: "predrilled", label: t.predrilled },
    { value: "nonPredrilled", label: t.nonPredrilled },
  ];
  const diameter = ev && Number.isFinite(ev.etaDiametro) ? String(ev.etaDiametro) : "";
  const diameterOptions = options.diameters.map((o) => ({ value: String(o.value), label: o.label }));
  const length = ev && Number.isFinite(ev.etaLunghezza) ? String(ev.etaLunghezza) : "";
  const lengthOptions = options.lengths.map((o) => ({ value: String(o.value), label: o.label }));
  const useWasher = ev ? ev.useRondella : values.washerCode != null;
  const washerCode = ev ? ev.rondellaCodice : (values.washerCode ?? "");
  const washerOptions = stringOptions(options.washers);

  return (
    <>
      <FormField
        label={t.family}
        footer={ui.texts.etaFamigliaEmptyWarn ? { type: "warning", message: ui.texts.etaFamigliaEmptyWarn } : undefined}
        disabled={disabled}
      >
        {({ labelId, disabled }) => (
          <Select
            inputId={labelId}
            name="etaFamiglia"
            options={familyOptions}
            value={familyOptions.find((o) => o.value === family) ?? null}
            isDisabled={disabled || familyOptions.length === 0}
            isSearchable={false}
            placeholder="—"
            formatOptionLabel={(o) => <FamilyCell family={o.value} />}
            onChange={(o) => o && o.value !== family && onScrewChange?.("family", o.value)}
          />
        )}
      </FormField>

      <FormField label={t.foro} disabled={disabled || ui.disabled.etaForo}>
        {({ labelId, disabled }) => (
          <>
            <Select
              // the box keeps the HTML control id: the tutorial finds it also while the ETA rule locks it (a disabled
              // react-select renders no hidden named input)
              id="etaForo"
              inputId={labelId}
              name="etaForo"
              options={foroOptions}
              value={foroOptions.find((o) => o.value === foro) ?? null}
              isDisabled={disabled}
              isInvalid={isMissing("etaForo", !foro)}
              isSearchable={false}
              placeholder=""
              onChange={(o) => o && o.value !== foro && set("preDrilled", o.value === "predrilled")}
            />
            <Note html={notes.foro} />
            {ui.texts.etaForoForced ? <MessageBox type="warning">{ui.texts.etaForoForced}</MessageBox> : null}
            {ui.visible.etaPlateHwNote ? <Note html={notes.plateHw} /> : null}
          </>
        )}
      </FormField>

      <FormField label={t.diameter} footer={{ type: "info", message: "mm" }} disabled={disabled}>
        {({ labelId, disabled }) => (
          <>
            <Select
              inputId={labelId}
              name="etaDiametro"
              options={diameterOptions}
              value={diameterOptions.find((o) => o.value === diameter) ?? null}
              isDisabled={disabled}
              isInvalid={isMissing("etaDiametro", !diameter)}
              isSearchable={false}
              placeholder=""
              onChange={(o) => o && o.value !== diameter && onScrewChange?.("diameter", o.value)}
            />
            {ui.texts.etaDiametroEmptyWarn ? (
              <MessageBox type="warning">{withoutWarningSign(ui.texts.etaDiametroEmptyWarn)}</MessageBox>
            ) : null}
            {ui.visible.etaMyKWarn ? <MessageBox type="warning">{notes.myk}</MessageBox> : null}
          </>
        )}
      </FormField>

      <FormField label={t.length} footer={{ type: "info", message: t.lengthUnit }} disabled={disabled}>
        {({ labelId, disabled }) => (
          <>
            <Select
              inputId={labelId}
              name="etaLunghezza"
              options={lengthOptions}
              value={lengthOptions.find((o) => o.value === length) ?? null}
              isDisabled={disabled}
              isInvalid={isMissing("etaLunghezza", !length)}
              isSearchable={false}
              placeholder=""
              onChange={(o) => {
                if (!o || o.value === length) return;
                const option = options.lengths.find((l) => String(l.value) === o.value);
                if (option?.screwId) set("screwId", option.screwId);
              }}
            />
            {ui.texts.etaLunghezzaEmptyWarn ? (
              <MessageBox type="warning">{withoutWarningSign(ui.texts.etaLunghezzaEmptyWarn)}</MessageBox>
            ) : null}
            <Note html={ui.texts.staggerNote ?? ""} />
            {ui.texts.screwLengthWarn ? (
              <MessageBox type="warning">{withoutWarningSign(ui.texts.screwLengthWarn)}</MessageBox>
            ) : null}
          </>
        )}
      </FormField>

      {ui.visible.rondellaField ? (
        <div className={styles.stack}>
          <Switch
            id={`${switchId}-washer`}
            checked={useWasher}
            disabled={disabled}
            onChange={(checked) => set("washerCode", checked ? washerCode || options.washers[0] || null : null)}
          >
            {t.washer} <Html text={t.washerUnit} />
          </Switch>
          {ui.visible.rondellaCodice ? (
            <Select
              aria-label={t.washer}
              options={washerOptions}
              value={washerOptions.find((o) => o.value === washerCode) ?? null}
              isDisabled={disabled}
              isSearchable={false}
              placeholder=""
              onChange={(o) => o && o.value !== washerCode && set("washerCode", o.value)}
            />
          ) : null}
        </div>
      ) : null}

      <Switch
        id={`${switchId}-autoSpacing`}
        checked={ev ? ev.autoSpacing : values.autoSpacing}
        disabled={disabled}
        onChange={(v) => set("autoSpacing", v)}
      >
        {t.autoSpacing}
      </Switch>
    </>
  );
};

// Memoized: it re-renders through its own props (the engine view among them) and contexts, not whenever its parent
// does.
export default memo(ConnectionCard);
